import React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button, PinnedActionBar } from "../../../components/ui";
import { PRECONSULTATION_STEPS } from "../preconsultationSteps";

/**
 * The parts of the wizard that are the same on every step: how far along it
 * says you are, what the step is called, and the bar at the foot that moves
 * you.
 *
 * Split out from the page because the page is already the whole of the state
 * machine — which step, which answers, what the server said — and reading that
 * next to a hundred lines of chrome makes neither legible.
 */

/* ── How far along ────────────────────────────────────────────────────── */

export const StepProgress: React.FC<{ index: number; total?: number }> = ({
  index,
  // How many steps this form actually has, which is not always all of them:
  // a practitioner who asks for no location and no health questions has a
  // shorter form, and a bar counting the steps that were never going to be
  // shown would sit at two thirds on the last one. See `stepsAsked`.
  total = PRECONSULTATION_STEPS.length,
}) => {
  const done = index + 1;

  return (
    // A width of its own from `sm:` up: on a wide screen the bar shares a row
    // with the step's name rather than running the width of the page above it
    // — see the row in `PreconsultationFormPage`. Left to `flex-1` there it
    // would take whatever the heading did not, which is a bar that changes
    // length every time the question does.
    <div className="flex items-center gap-3 sm:w-[320px] sm:flex-shrink-0">
      {/*
        A bar and a count, not one or the other. The bar is read at a glance —
        it is the answer to "am I nearly done" — and the count is what somebody
        checks when the bar is not moving as fast as they hoped.

        `aria-hidden` on the bar and the count carried by the label below: a
        screen reader announcing a progressbar and then the same figure again
        would say it twice.
      */}
      <div
        aria-hidden="true"
        className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-[#E4E7EC]"
      >
        <div
          className="h-full rounded-full bg-brand transition-[width] duration-300 ease-out"
          style={{ width: `${(done / total) * 100}%` }}
        />
      </div>
      <span
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-valuetext={`Step ${done} of ${total}`}
        className="flex-shrink-0 text-[13px] text-ink-muted"
      >
        {done}/{total}
      </span>
    </div>
  );
};

/* ── What this step is called ─────────────────────────────────────────── */

export const StepHeading: React.FC<{
  /** Its position in the steps this practitioner asks, counting from one. */
  step: number;
  label: string;
  title: string;
}> = ({ step, label, title }) => (
  <div className="min-w-0">
    {/*
      "STEP 2 : Location" — the number from the position and the name from the
      step, so it cannot drift from the count on the bar beside it.

      Not `uppercase`. The design sets "STEP" in capitals and the name of the
      step as it is written, and a rule that shouts the whole line loses that
      distinction — it is the word STEP that is the label and the name that is
      the information.
    */}
    <p className="text-[13px] sm:text-[15px] font-medium tracking-[0.02em] text-brand">
      {`STEP ${step} : ${label}`}
    </p>
    {/*
      An `h1` per step rather than one for the form. Each of these is a
      different question and the reader is only ever on one of them — a heading
      that stayed "Pre-consultation form" while the page under it changed six
      times would be the least useful line on the screen.
    */}
    <h1 className="mt-1 text-[24px] sm:text-[30px] font-semibold tracking-[-0.01em] text-[#0A4A52]">
      {title}
    </h1>
  </div>
);

/* ── The bar that moves you ───────────────────────────────────────────── */

export interface StepActionsProps {
  /** Hidden on the first step, where there is nowhere to go back to. */
  onBack?: () => void;
  /** "Continue" on the six, "Submit" on the last. */
  nextLabel: string;
  /** The submit is with the server: the button says so and refuses a second. */
  busy?: boolean;
}

/**
 * Back and Continue, pinned to the bottom edge of the screen.
 *
 * Pinned rather than sitting under the last card, and that is the one layout
 * decision on this form worth arguing about. A long step scrolls, and a
 * Continue at the end of it is a button somebody has to go looking for after
 * they have finished answering — on a phone, past a keyboard that has just
 * closed. Held at the foot it is where their thumb already is, and the step
 * is finished when they say it is rather than when they reach the bottom.
 *
 * On a phone the glass is no longer written here at all: `PinnedActionBar` is,
 * and it blurs what passes under without laying any fill over it — the same
 * bar the dashboard's own forms end in, so a practitioner and their client are
 * looking at one piece of software. What is below is what this bar wears from
 * `sm:` up, where there is a window rather than a phone, and it is still the
 * enquiry form's to the number — the two forms arrive from the same
 * practitioner and a reader who meets both should not be able to tell they
 * were built apart:
 *
 *   - `#FFFFFF26` — white at 15% rather than solid, so the field under it stays
 *     half visible and the bar reads as glass laid over the form rather than as
 *     a panel cutting the end off it.
 *   - `backdrop-blur` is 8px: enough to soften what is behind without
 *     dissolving it, where past about 16px text stops reading as text.
 *   - The bottom padding is 2rem rather than 1rem, so a strip of glass shows
 *     *under* the buttons as well as behind them. That strip is the only place
 *     the blurred field beneath is actually seen — the buttons cover the rest.
 *
 * No rule along the top. A tint and a blur are already a change of surface, and
 * a hairline over them only draws a second edge where the card below has just
 * drawn one.
 *
 * `-mx-5` cancels the shell's phone gutter so the glass runs edge to edge, and
 * `sm:-mx-8` does the same against the wider one — the bar is held at every
 * width, not only on a phone. It used to go `static` from `sm:` up and sit
 * centred under the last card, on the reasoning that a desktop window has room
 * for the whole step. It does not: Review is two thousand pixels of cards and
 * Your day runs a third of a window past the fold, so on exactly the steps that
 * take longest to answer the one thing there was to press sat below it — and
 * somebody who had just finished the last question had to scroll back through a
 * page they had already read to find the button. Held, Continue is in the same
 * place on every step at every size, which is where the welcome's own button
 * lands and is the whole of why it is held there at all.
 *
 * Which means there is something under the glass on a wide screen too, so the
 * tint and the blur stay rather than being dropped from `sm:` up. What changes
 * is the room the bar draws below itself: 2rem and the safe area on a phone,
 * the column's own 1.5rem from `sm:` up. The shell leaves the foot of the page
 * to the bar at both widths now — see `VERTICAL` — so a strip of page can
 * never show beneath something meant to sit against the edge.
 *
 * Continue carries no `onClick`. It is the form's submit button and the form
 * around it is what handles the press — which is also what makes Enter in a
 * text field move the step on. Given both, a press would go through twice, and
 * the second pass would read the step it had just left as valid and skip the
 * next one.
 *
 * And it is the app's own `Button` rather than one drawn here. It used to wear
 * a pale fill with dark text of its own, which made the one thing every screen
 * of this form is asking somebody to press the only action in the app that did
 * not look like an action: the enquiry form's Submit, the dashboard's Save and
 * this Continue arrive on the same phone from the same practitioner, and two
 * of them were filled teal with white text while the third was not. `size="lg"`
 * is the enquiry form's submit exactly. What is still written here is the
 * layout — full width beside the Back square on a phone, a fixed width centred
 * from `sm:` up — which is this bar's business and not the variant's.
 */
export const StepActions: React.FC<StepActionsProps> = ({
  onBack,
  nextLabel,
  busy = false,
}) => (
  <PinnedActionBar
    className={
      "-mx-5 mt-4 sm:mt-2 " +
      "bg-[#FFFFFF26] backdrop-blur " +
      "px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4 " +
      "sm:-mx-8 sm:px-8 sm:pb-6 sm:pt-3"
    }
  >
    {/* The pair travels together from `sm:` up — a Back stranded at the left
        edge of a wide screen with Continue in the middle reads as two
        unrelated controls rather than as one way through. */}
    <div className="flex items-stretch gap-3 sm:mx-auto sm:w-auto sm:justify-center">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className={
            "flex w-[56px] flex-shrink-0 items-center justify-center rounded-[8px] " +
            "border border-line bg-white text-ink transition-colors hover:bg-[#F2F5F7] " +
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          }
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
      )}
      {/* `loading` is what disables it while the answers are with the server,
          and what puts the spinner over the label without the button changing
          size under the thumb. */}
      <Button
        type="submit"
        size="lg"
        loading={busy}
        className="min-w-0 flex-1 sm:min-w-[264px] sm:flex-none"
      >
        {nextLabel}
      </Button>
    </div>
  </PinnedActionBar>
);

/** The arrow the welcome screen's button carries. Here so the two buttons on
 *  this form are drawn from one place. */
export const ForwardArrow = <ArrowRight size={18} aria-hidden="true" />;
